import React, { useEffect, useState, useMemo } from 'react';
import { MdSearch } from 'react-icons/md';
import EbayAspectForm from './EbayAspectForm';
import { gql, useSuspenseFragment } from '@apollo/client';
import { api } from '../../utils/api';
import Button from '../common/Button';
import { CategorySelectionModal } from '../EbayCategoryTree';
import { EbayAttributesSection_ListingFragment } from '../../../codegen/graphql';
import { getTranslatedValue } from '../common/TranslatedValue';

export const EBAY_ATTRIBUTES_SECTION_FRAGMENT = gql`
  fragment EbayAttributesSection_listing on AdExternalListing {
    id
    lang
    externalData
    ad {
      title
      title80
      withVariations
    }
    shop {
      id
    }
  }
`;


interface CategorySuggestion {
  category: {
    categoryId: string;
    categoryName: string;
  };
}

interface EbayAttributesSectionProps {
  listing: EbayAttributesSection_ListingFragment;
  onExternalDataChange: (newExternalData: Record<string, any>) => void;
}

export function EbayAttributesSection({ listing, onExternalDataChange }: EbayAttributesSectionProps) {
  const { data: fragmentData} = useSuspenseFragment<EbayAttributesSection_ListingFragment>({
    fragment: EBAY_ATTRIBUTES_SECTION_FRAGMENT,
    fragmentName: 'EbayAttributesSection_listing',
    from: listing,
  });

  const externalData = fragmentData.externalData || {};

  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<CategorySuggestion[]>([]);
  const [itemAspects, setItemAspects] = useState<any[]>([]);
  const [isCategoryTreeOpen, setIsCategoryTreeOpen] = useState(false);

  const handleFetchCategoriesSuggestions = async () => {
    setIsLoading(true);
    try {
      const title80 = getTranslatedValue(fragmentData.ad.title80, fragmentData.lang ?? 'FR');
      const params = new URLSearchParams();
      params.set('shop_id', fragmentData.shop.id);
      params.set('lang', encodeURIComponent(fragmentData.lang));
      params.set('display_language', 'fr-FR');
      const response = await api.get(
        `/ebay/category_suggestions/${encodeURIComponent(title80.replace(/\//g, '-'))}?${params.toString()}`);
      const data = await response.json();
      setSuggestions(data.categorySuggestions || []);
    } catch (error) {
      console.error('Error fetching category suggestions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategorySelect = (categoryId: string, categoryName: string) => {
    onExternalDataChange({
      ...externalData,
      category: {
        id: categoryId,
        name: categoryName
      }
    });
    setSuggestions([]);
  };

  const handleShowCategoryTree = () => {
    setIsCategoryTreeOpen(true);
  };

  useEffect(() => {
    const fetchItemAspects = async () => {
      const response = await api.get(`/ebay/item_aspects/${externalData.category?.id}?shop_id=${fragmentData.shop.id}&lang=${encodeURIComponent(fragmentData.lang)}`);
      const data = await response.json();
      setItemAspects(data.aspects || []);
    };

    if (externalData.category?.id) {
      fetchItemAspects();
    }
  }, [externalData.category?.id]);

  const handleAspectChange = (updatedAspect: any) => {

    const newAspects = {
      ...externalData.aspects,
      [updatedAspect.name]: updatedAspect.value
    };

    if (updatedAspect.value.length === 0) {
      delete newAspects[updatedAspect.name];
    }

    onExternalDataChange({
      ...externalData,
      aspects: newAspects
    });
  };


  const {
    mainAspects,
    variationsAspects,
  } = useMemo(() => itemAspects.reduce<{
      mainAspects: any[],
      variationsAspects: any[],
    }>((acc, aspect) => {
      if (aspect.aspectConstraint.aspectRequired === true) {
        if (
          fragmentData.ad.withVariations
          && aspect.aspectConstraint.aspectEnabledForVariations === true
          // todo : fix
          && false
        ) {
          acc.variationsAspects.push(aspect);
        } else {
          acc.mainAspects.push(aspect);
        }
      } else {
        acc.mainAspects.push(aspect);
      }
      return acc;
    }, { mainAspects: [], variationsAspects: [] }),
    [itemAspects, fragmentData.ad.withVariations]
  );


  return (
    <div className="bg-white shadow-sm rounded-lg p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">eBay Attributes</h2>
      
      <div className="space-y-4">
        {/* Category Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category
          </label>
          
          <div className="flex items-center gap-2 mb-2">
            <div className="grow">
              {externalData.category ? (
                <div className="p-2 bg-gray-50 rounded-sm border">
                  {externalData.category.name}
                </div>
              ) : (
                <div className="text-gray-500 italic">No category selected</div>
              )}
            </div>
            <Button
              onClick={handleFetchCategoriesSuggestions}
              disabled={isLoading}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-md ${
                isLoading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              <MdSearch size={20} />
              {isLoading ? 'Loading...' : 'Fetch suggestions'}
            </Button>

            <Button
              onClick={handleShowCategoryTree}
              disabled={isLoading}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-md ${
                isLoading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              <MdSearch size={20} />
              {isLoading ? 'Loading...' : 'Select category'}
            </Button>
          </div>

          {/* Category Suggestions */}
          {suggestions.length > 0 && (
            <div className="mt-2 border rounded-md divide-y">
              {suggestions.map((suggestion, index) => (
                <button
                  key={suggestion.category.categoryId}
                  onClick={() => handleCategorySelect(
                    suggestion.category.categoryId,
                    suggestion.category.categoryName
                  )}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors"
                >
                  {suggestion.categoryTreeNodeAncestors.map(ancestor => ancestor.categoryName).join(' > ')} {'>'} {suggestion.category.categoryName}
                </button>
              ))}
            </div>
          )}
        </div>

        {isCategoryTreeOpen && (
          <CategorySelectionModal
            isOpen={isCategoryTreeOpen}
            onClose={() => setIsCategoryTreeOpen(false)}
            onSelect={(categoryNode) => handleCategorySelect(categoryNode.category.categoryId, categoryNode.category.categoryName)}
            shopId={fragmentData.shop.id}
            lang={fragmentData.lang}
          />
        )}

        {itemAspects.length > 0 && (
          <EbayAspectForm
            aspects={mainAspects}
            data={externalData.aspects}
            onChange={handleAspectChange}
          />
        )}
      </div>
    </div>
  );
}
